import Assign from 'core-js-pure/features/object/assign';
import {
  KustomerWindow,
  Kustomer,
  KustomerWidgetWindow,
  KustomerWidgetEventTypes,
} from 'widget/types';
import { setupIframeForApi, getIframeForWidgetMain } from 'helpers/browser';
import { runQueue } from 'helpers/queue';
import { DescribeCustomerParameters } from 'core_main/sdk/describe_customer/types';
import { DescribeConversationParameters } from 'core_main/sdk/describe_conversation/types';
import { LoginParameters } from 'core_main/sdk/login/types';
import { IsLoggedInParameters } from 'core_main/sdk/is_logged_in/types';
import { installPolyfills } from 'polyfills/helpers';
import { kustomerWidgetEventTypes } from '../widget_main/globals/constants';
import {
  CreateConversationEventDetail,
  CreateConversationWidgetApiParameters,
  StartParameters,
  OpenConversationByIdWidgetApiParameters,
} from './types';
import cssReset from './cssReset';
import {
  makeIncompleteStartError,
  parseStartParameters,
  setKustomerStyleSheetOnParent,
} from './helpers';
import { DEFAULT_Z_INDEX, publicConstants } from './constants';

const kustomerWindow: KustomerWindow = window;
const kustomer = kustomerWindow.Kustomer as Kustomer;

const setupWindow = () => {
  kustomerWindow.Kustomer = Assign(kustomer, {
    constants: publicConstants,
    startFinished: false,
    startParameters: {},
    startCallback: null,
    listeners: {},
    addListener(eventType: KustomerWidgetEventTypes, cb) {
      if (!kustomerWidgetEventTypes[eventType]) {
        return console.error(`${eventType} is not a valid event name`);
      }
      if (typeof cb !== 'function') {
        return undefined;
      }

      this.listeners[eventType] = this.listeners[eventType] || [];
      this.listeners[eventType].push(cb);
      return undefined;
    },
    removeListener(eventType: KustomerWidgetEventTypes, cb) {
      if (!this.listeners[eventType] || typeof cb !== 'function') {
        return;
      }
      this.listeners[eventType] = this.listeners[eventType].filter(
        (callback) => callback !== cb,
      );
    },
    start(parameters: StartParameters, cb) {
      this.startParameters = parseStartParameters(parameters);
      this.startCallback = cb;
      if (getIframeForWidgetMain()) {
        return;
      }
      setKustomerStyleSheetOnParent();

      const zIndex = this.startParameters.zIndex || DEFAULT_Z_INDEX;
      const position = this.startParameters.chatIconPosition;
      const horizontalPadding =
        position.alignment === 'right'
          ? `right: ${position.horizontalPadding}px`
          : `left: ${position.horizontalPadding}px`;

      // We load sentry here using a script tag, as apposed to installing it via npm, so that it defers loading the
      // full sdk until an error happens. If no error happens, sentry will not load, saving client resources.
      // see https://docs.sentry.io/platforms/javascript/install/lazy-load-sentry/ for more info
      const SENTRY_KEY = kustomer.s || '';

      // Attempt to parse the api key to log in Sentry
      let parsedApiKey;
      try {
        parsedApiKey = JSON.parse(atob(kustomer.a.split('.')[1]));
      } catch (e) {
        // Do nothing. We just won't log meta data to Sentry
      }

      setupIframeForApi(
        'kustomer-ui-sdk-iframe',
        `display: none; border: 0; width: 0px; height: 0px; position: fixed; bottom: ${position.verticalPadding}px; ${horizontalPadding}; z-index: ${zIndex};`,
        `
        <style>${cssReset}</style>
        <body class="align-chat-icon-${position.alignment}" data-kt="chat-icon">
          <div id="root"></div>
          <base target="_parent">
            <script
              src="${process.env.CHAT_WEB_BASE_URL}/core.js"
              data-kustomer-api-key="${kustomer.a}"
              data-kustomer-sentry-key="${SENTRY_KEY}"
            >
            </script>
            <script
              async
              src="${process.env.KUSTOMER_WIDGET_MAIN_INCLUDE}">
            </script>
            ${
              SENTRY_KEY
                ? `
             <script
              src="${`https://js.sentry-cdn.com/${SENTRY_KEY}.min.js`}"
              crossorigin="anonymous"
              >
            </script>
            <script>
              Sentry.onLoad(() => {
                Sentry.init({
                  environment: '${process.env.SENTRY_ENV}'
                });
                ${
                  parsedApiKey
                    ? `
                Sentry.setTag("org", "${parsedApiKey.orgName}");
                Sentry.setContext("org", ${JSON.stringify({
                  orgId: parsedApiKey.org,
                  orgName: parsedApiKey.orgName,
                })});
                `
                    : ''
                }
              });
            </script>
            `
                : ''
            }
        </body>`,
        'Kustomer Widget SDK Iframe',
      );
    },
    open(callback) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerOpen', {
          detail: { callback },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    close(callback) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerClose', {
          detail: { callback },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    login(parameters: LoginParameters, callback) {
      const iframe = getIframeForWidgetMain();

      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerLogin', {
          detail: { parameters, callback },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    isLoggedIn(parameters: IsLoggedInParameters, callback) {
      const iframe = getIframeForWidgetMain();
      const contentWindow = iframe?.contentWindow as KustomerWidgetWindow;
      if (iframe && iframe.contentWindow) {
        contentWindow.KustomerCore?.isLoggedIn(parameters, callback);
      }
    },
    logout(cb) {
      const iframe = getIframeForWidgetMain();
      const contentWindow = iframe?.contentWindow as KustomerWidgetWindow;
      if (iframe && contentWindow) {
        contentWindow.KustomerCore?.logout(cb);
      }
    },
    describeCustomer(parameters: DescribeCustomerParameters, cb) {
      const iframe = getIframeForWidgetMain();
      const contentWindow = iframe?.contentWindow as KustomerWidgetWindow;
      if (iframe && contentWindow) {
        contentWindow.KustomerCore?.describeCustomer(parameters, cb);
      }
    },
    describeConversation(parameters: DescribeConversationParameters, cb) {
      const iframe = getIframeForWidgetMain();
      const contentWindow = iframe?.contentWindow as KustomerWidgetWindow;
      if (iframe && contentWindow) {
        contentWindow.KustomerCore?.describeConversation(parameters, cb);
      }
    },
    isChatAvailable() {
      const iframe = getIframeForWidgetMain();
      const contentWindow = iframe?.contentWindow as KustomerWidgetWindow;
      if (iframe && contentWindow) {
        return contentWindow.KustomerCore?.isChatAvailable();
      }

      return undefined;
    },
    createConversation(
      parameters: CreateConversationWidgetApiParameters,
      callback,
    ) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const message = parameters?.message;
        const payload = parameters?.payload;
        const assistantId = parameters?.assistantId;
        const dialogId = parameters?.dialogId;

        const detail: CreateConversationEventDetail = {
          callback,
          message,
          payload,
          assistantId,
          dialogId,
        };

        const event = new CustomEvent('kustomerCreateConversation', {
          detail,
        });

        iframe.contentWindow.dispatchEvent(event);
      }
    },
    openConversationById(
      parameters: OpenConversationByIdWidgetApiParameters,
      callback?,
    ) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerOpenConversationById', {
          detail: { conversationId: parameters?.conversationId, callback },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    getOpenConversations(callback) {
      if (!this.startFinished) {
        const errorMessage = makeIncompleteStartError('getOpenConversations');
        console.error(errorMessage);
        if (typeof callback === 'function') {
          callback(null, new Error(errorMessage));
        }
        return;
      }

      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerGetOpenConversations', {
          detail: { callback },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    openKbArticle(articleId: string, cb?) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerOpenKbArticle', {
          detail: { articleId, cb },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
    openKbCategory(categoryId: string, cb?) {
      const iframe = getIframeForWidgetMain();
      if (iframe && iframe.contentWindow) {
        const event = new CustomEvent('kustomerOpenKbCategory', {
          detail: { categoryId, cb },
        });
        iframe.contentWindow.dispatchEvent(event);
      }
    },
  });

  if (kustomer) {
    runQueue(kustomer);
  }
};

installPolyfills().then(setupWindow);
